"use client";

import { ComponentProps } from "react";
import { Callout as RadixCallout } from "@radix-ui/themes";
import { joinClassNames } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";

interface StyleProps {
  className?: string;
  shopsiteClasses?: string;
  whitelabelClasses?: string;
  sharedClasses?: string;
  border?: boolean;
}

const CalloutRoot = ({
  className = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  sharedClasses = "",
  children,
  color,
  border,
  ref,
  ...props
}: ComponentProps<typeof RadixCallout.Root> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteVariantClasses =
    color === "green"
      ? "bg-green-100 text-green-800"
      : color === "yellow"
      ? "bg-yellow-100 text-yellow-800"
      : color === "red"
      ? "bg-red-100 text-red-500"
      : color === "orange"
      ? "bg-orange-100 text-orange-800"
      : "bg-indigo-100 text-indigo-800";
  const shopsiteBaseClasses = joinClassNames(
    "flex items-center gap-x-2 gap-y-1 p-4 rounded-lg",
    border ? "ring-2 ring-yellow-800" : ""
  );

  if (isShopsite) {
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={joinClassNames(
          shopsiteBaseClasses,
          shopsiteVariantClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <RadixCallout.Root
      ref={ref as React.Ref<HTMLDivElement>}
      color={color}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
      {...props}
    >
      {children}
    </RadixCallout.Root>
  );
};

CalloutRoot.displayName = RadixCallout.Root.displayName;

const CalloutIcon = ({
  className = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  sharedClasses = "",
  children,
  ref,
  ...props
}: ComponentProps<typeof RadixCallout.Icon> & StyleProps) => {
  const isShopsite = useIsShopsite();

  const shopsiteBaseClasses = "flex items-center justify-center";

  if (isShopsite) {
    return (
      <div
        ref={ref}
        className={joinClassNames(
          shopsiteBaseClasses,
          className,
          sharedClasses,
          shopsiteClasses
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <RadixCallout.Icon
      ref={ref}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
      {...props}
    >
      {children}
    </RadixCallout.Icon>
  );
};

CalloutIcon.displayName = RadixCallout.Icon.displayName;

const CalloutText = ({
  className = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  sharedClasses = "",
  children,
  ref,
  ...props
}: ComponentProps<typeof RadixCallout.Text> & StyleProps) => {
  const isShopsite = useIsShopsite();

  if (isShopsite) {
    return (
      <div
        ref={ref}
        className={joinClassNames(className, sharedClasses, shopsiteClasses)}
      >
        {children}
      </div>
    );
  }

  return (
    <RadixCallout.Text
      ref={ref}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
      {...props}
    >
      {children}
    </RadixCallout.Text>
  );
};

CalloutText.displayName = RadixCallout.Text.displayName;

export { CalloutRoot, CalloutIcon, CalloutText };
